import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  boardRequiredApprovalsPatchSchema,
  boardRequiredApprovalsSchema,
  type ApiError,
  type BoardRequiredApprovals,
  type BoardRequiredApprovalsPatch,
} from '@molt/shared';

import { patch } from './api';
import { queryKeys } from './query-keys';

/**
 * `PATCH /boards/:id/required-approvals` — the PR-list approvals threshold.
 * Field-scoped (SC-4). Lowering it can mark cards met, which changes the card
 * fronts' badges and every card back's `approvals` block, so everything under
 * the board and every card query is refetched.
 */
export function updateBoardRequiredApprovals(
  boardId: string,
  body: BoardRequiredApprovalsPatch,
): Promise<BoardRequiredApprovals> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/required-approvals`,
    boardRequiredApprovalsSchema,
    boardRequiredApprovalsPatchSchema.parse(body),
  );
}

export function useUpdateBoardRequiredApprovals(
  boardId: string,
): UseMutationResult<BoardRequiredApprovals, ApiError, BoardRequiredApprovalsPatch> {
  const queryClient = useQueryClient();

  return useMutation<BoardRequiredApprovals, ApiError, BoardRequiredApprovalsPatch>({
    mutationFn: (body) => updateBoardRequiredApprovals(boardId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards.all() });
    },
  });
}
